import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { MermaIcon, ThroughputIcon } from '../../dum';
import { formatFloat } from '../../../helpers';
// All five are types and nothing here imports a value from this module, so the
// import says type. That also cleared a no-unused-vars warning on
// ThroughputUnit, which the rule did not credit as used by UNIT_LABEL below.
import type {
    AxisGrade,
    FlagSeverity,
    LineGrade,
    ProductionGrade,
    ThroughputUnit,
} from './performance-flags';

// MUI palette keys per severity. 'ok' never renders a chip.
const CHIP_COLOR: Record<
    Exclude<FlagSeverity, 'ok'>,
    'warning' | 'error'
> = {
    warn: 'warning',
    bad: 'error',
};

const formatPercent = (ratio: number): string =>
    `${formatFloat(ratio * 100, 0)}%`;

// The unit that actually decided the grade. Never assumed: a line only reads in
// bultos/hr when both the run and its history carry bultos.
export const UNIT_LABEL: Record<ThroughputUnit, string> = {
    bultos: 'bultos/hr',
    kilos: 'kg/hr',
};

// A machine-level baseline mixes every product the machine makes, so it is a
// weaker comparison than the combo's own history. It is used when the combo
// cannot muster MIN_BASELINE_RUNS, and it always says so.
const baselineNote = (line: LineGrade): string | null =>
    line.baselineSource === 'machine'
        ? 'Comparado contra el promedio de la MÁQUINA (este producto no tiene suficientes corridas propias)'
        : null;

// "62% del promedio" reads better than a bare ratio, and the direction word
// changes per axis because low is bad for one and high for the other.
function throughputTooltip(line: LineGrade): string {
    const { throughput } = line;
    if (throughput.ratio === null) return '';
    const unit = UNIT_LABEL[throughput.unit];
    // The unit that was not graded still gets a line: it is the number most
    // people already have in their head, and hiding it invites "62% of what?".
    const other =
        throughput.unit === 'bultos' && throughput.kilos.value !== null
            ? `Equivale a ${formatFloat(throughput.kilos.value)} kg/hr`
            : null;
    return [
        `Rendimiento ${formatPercent(throughput.ratio)} del promedio`,
        `Esta corrida: ${formatFloat(throughput.value ?? 0)} ${unit}`,
        `Promedio histórico: ${formatFloat(throughput.baseline ?? 0)} ${unit}`,
        other,
        `Base: ${throughput.baselineRuns} corridas (sin contar ésta)`,
        baselineNote(line),
    ]
        .filter(Boolean)
        .join('\n');
}

function mermaTooltip(line: LineGrade): string {
    const { merma } = line;
    if (merma.value === null) return '';
    const head =
        merma.ratio === null
            ? 'Merma sobre un histórico sin desperdicio'
            : `Merma ${formatPercent(merma.ratio)} del promedio`;
    return [
        head,
        `Esta corrida: ${formatPercent(merma.value)}`,
        merma.baseline === null
            ? null
            : `Promedio histórico: ${formatPercent(merma.baseline)}`,
        `Base: ${merma.baselineRuns} corridas (sin contar ésta)`,
        baselineNote(line),
    ]
        .filter(Boolean)
        .join('\n');
}

interface IPerformanceFlagChips {
    grade: ProductionGrade | undefined;
    size?: 'small' | 'medium';
}

/**
 * The two independent under-performance flags for a production. Throughput and
 * merma are shown separately on purpose — a run can be slow but clean, or fast
 * and wasteful, and collapsing them into one badge would hide which.
 *
 * Nothing renders when a production is fine or ungradable; an absent chip is
 * never a claim that a run was good, only that it was not flagged.
 */
export function PerformanceFlagChips({
    grade,
    size = 'small',
}: IPerformanceFlagChips) {
    if (!grade) return null;

    const chips: React.ReactNode[] = [];

    const push = (
        key: string,
        line: LineGrade,
        axis: AxisGrade,
        label: string,
        icon: React.ReactElement,
        tooltip: string,
    ) => {
        if (axis.severity === 'ok') return;
        chips.push(
            <Tooltip
                key={key}
                title={<Box sx={{ whiteSpace: 'pre-line' }}>{tooltip}</Box>}
            >
                <Chip
                    size={size}
                    icon={icon}
                    color={CHIP_COLOR[axis.severity]}
                    variant={'outlined'}
                    label={label}
                />
            </Tooltip>,
        );
    };

    if (grade.worstThroughput) {
        const line = grade.worstThroughput;
        push(
            'throughput',
            line,
            line.throughput,
            line.throughput.ratio === null
                ? 'Rendimiento bajo'
                : `Rendimiento ${formatPercent(line.throughput.ratio)}`,
            <ThroughputIcon />,
            throughputTooltip(line),
        );
    }

    if (grade.worstMerma) {
        const line = grade.worstMerma;
        push(
            'merma',
            line,
            line.merma,
            line.merma.value === null
                ? 'Merma alta'
                : `Merma ${formatPercent(line.merma.value)}`,
            <MermaIcon />,
            mermaTooltip(line),
        );
    }

    if (chips.length === 0) return null;

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
            }}
        >
            {chips}
        </Box>
    );
}

/**
 * Compact per-row marker for the card's product x machine sub-table and for the
 * embedded minified table: the same two axes, icon-only, so a row can be located
 * without the header chip's width.
 */
export function PerformanceFlagMarks({
    line,
}: {
    line: LineGrade | undefined;
}) {
    if (!line) return null;

    const marks: React.ReactNode[] = [];
    if (line.throughput.severity !== 'ok') {
        marks.push(
            <Tooltip
                key={'throughput'}
                title={
                    <Box sx={{ whiteSpace: 'pre-line' }}>
                        {throughputTooltip(line)}
                    </Box>
                }
            >
                <ThroughputIcon
                    fontSize={'small'}
                    color={CHIP_COLOR[line.throughput.severity]}
                />
            </Tooltip>,
        );
    }
    if (line.merma.severity !== 'ok') {
        marks.push(
            <Tooltip
                key={'merma'}
                title={
                    <Box sx={{ whiteSpace: 'pre-line' }}>
                        {mermaTooltip(line)}
                    </Box>
                }
            >
                <MermaIcon
                    fontSize={'small'}
                    color={CHIP_COLOR[line.merma.severity]}
                />
            </Tooltip>,
        );
    }

    if (marks.length === 0) return null;

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                ml: 0.5,
                verticalAlign: 'middle',
            }}
        >
            {marks}
        </Box>
    );
}
